import React from 'react';
import { useTradingContext } from '../../context/TradingContext';
import { ListFilter, TrendingUp, TrendingDown } from 'lucide-react';

export default function OrderBookPanel() {
  const { orderBook, currentMarketData } = useTradingContext();
  
  // Dense orderbook (more levels, smaller rows)
  const asks = (orderBook.asks || []).slice(0, 16).reverse();
  const bids = (orderBook.bids || []).slice(0, 16);
  
  const calculateTotal = (book) => {
    let acc = 0;
    return book.map(level => {
      acc += parseFloat(level[1] || 0);
      return { 
        price: parseFloat(level[0] || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }), 
        amount: parseFloat(level[1] || 0).toFixed(4), 
        total: acc.toFixed(3) 
      };
    });
  };

  const processedAsks = calculateTotal(asks);
  const processedBids = calculateTotal(bids);
  
  const maxTotal = Math.max(
    processedAsks.length > 0 ? parseFloat(processedAsks[processedAsks.length - 1].total) : 0,
    processedBids.length > 0 ? parseFloat(processedBids[processedBids.length - 1].total) : 0,
    0.001
  );

  return (
    <div className="trading-card flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between bg-slate-950/20">
        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Order Book</span>
        <div className="flex gap-1">
            <button className="w-5 h-5 rounded bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 text-emerald-500 transition-colors hover:bg-emerald-500/20"><TrendingUp size={10}/></button>
            <button className="w-5 h-5 rounded bg-rose-500/10 flex items-center justify-center border border-rose-500/20 text-rose-500 transition-colors hover:bg-rose-500/20"><TrendingDown size={10}/></button>
        </div>
      </div>

      {/* Table Headers */}
      <div className="px-4 py-1.5 grid grid-cols-3 text-[9px] font-black text-slate-600 uppercase tracking-widest border-b border-white/5">
        <span>Price</span>
        <span className="text-right">Size</span>
        <span className="text-right">Total</span>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide flex flex-col">
        {/* Asks (Sells) */}
        <div className="flex flex-col-reverse px-0.5">
          {processedAsks.map((ask, idx) => (
            <div key={`ask-${idx}`} className="grid grid-cols-3 py-0.5 px-3 relative group cursor-pointer transition-colors hover:bg-white/[0.03]">
              <div className="absolute right-0 top-0 bottom-0 bg-rose-500/10 transition-all duration-500" style={{ width: `${(parseFloat(ask.total) / maxTotal) * 100}%` }}></div>
              <span className="text-[11px] font-bold text-rose-400 relative z-10 font-outfit tracking-tighter">{ask.price}</span>
              <span className="text-[11px] font-medium text-slate-300 text-right relative z-10 font-outfit tracking-tighter">{ask.amount}</span>
              <span className="text-[11px] font-medium text-slate-600 text-right relative z-10 font-outfit tracking-tighter">{ask.total}</span>
            </div>
          ))}
        </div>
        
        {/* Mid Price & Spread */}
        <div className="py-2.5 px-4 bg-slate-950/40 border-y border-white/5 flex flex-col items-center justify-center">
          <div className="flex items-center gap-2">
            <span className={`text-lg font-black font-outfit tracking-tight ${currentMarketData.changePercent >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {currentMarketData.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
            {currentMarketData.changePercent >= 0 ? <TrendingUp size={14} className="text-emerald-500" /> : <TrendingDown size={14} className="text-rose-500" />}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">Spread: 0.01%</p>
            <span className="text-[10px] text-slate-600 font-bold leading-none">${currentMarketData.price.toLocaleString()}</span>
          </div>
        </div>

        {/* Bids (Buys) */}
        <div className="flex flex-col px-0.5">
          {processedBids.map((bid, idx) => (
            <div key={`bid-${idx}`} className="grid grid-cols-3 py-0.5 px-3 relative group cursor-pointer transition-colors hover:bg-white/[0.03]">
              <div className="absolute right-0 top-0 bottom-0 bg-emerald-500/10 transition-all duration-500" style={{ width: `${(parseFloat(bid.total) / maxTotal) * 100}%` }}></div>
              <span className="text-[11px] font-bold text-emerald-400 relative z-10 font-outfit tracking-tighter">{bid.price}</span>
              <span className="text-[11px] font-medium text-slate-300 text-right relative z-10 font-outfit tracking-tighter">{bid.amount}</span>
              <span className="text-[11px] font-medium text-slate-600 text-right relative z-10 font-outfit tracking-tighter">{bid.total}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
